import React from 'react';
import { PlusCircle, MinusCircle, Bed, Sofa, BedDouble, Baby } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

const bedsName = {
  single: 'Single',
  double: 'Double',
  sofa_bed: 'Sofa Bed',
  bunk_bed: 'Bunk Bed',
  crib: 'Crib',
};

const BedConfigurator = ({ beds, onChange, onGuestCountChange, onBlur }) => {
  //   const { state } = useContract();
  //   const { language } = state;

  const calculateGuestCount = (bedList) => {
    return bedList.reduce((total, bed) => {
      let guestPerBed = 0;
      switch (bed.type) {
        case 'single':
          guestPerBed = 1;
          break;
        case 'double':
          guestPerBed = 2;
          break;
        case 'sofa_bed':
          guestPerBed = 1;
          break;
        case 'bunk_bed':
          guestPerBed = 2;
          break;
        case 'crib':
          guestPerBed = 1;
          break;
        default:
          guestPerBed = 0;
      }
      return total + guestPerBed * bed.count;
    }, 0);
  };

  const addBed = (type) => {
    const newBed = {
      id: uuidv4(),
      type,
      count: 1,
    };

    const updatedBeds = [...beds, newBed];

    onChange(updatedBeds); // Update parent component's state
    onGuestCountChange(calculateGuestCount(updatedBeds)); // Update guest count
  };

  const removeBed = (id) => {
    const updatedBeds = beds.filter((bed) => bed.id !== id);
    onChange(updatedBeds);
    onGuestCountChange(calculateGuestCount(updatedBeds));
  };

  const updateBedCount = (id, increment) => {
    const updatedBeds = beds.map((bed) => {
      if (bed.id === id) {
        const newCount = increment ? bed.count + 1 : Math.max(1, bed.count - 1);
        return { ...bed, count: newCount };
      }
      return bed;
    });

    onChange(updatedBeds);
    onGuestCountChange(calculateGuestCount(updatedBeds));
  };

  const getBedIcon = (type) => {
    switch (type) {
      case 'single':
        return <Bed className="w-5 h-5" />;
      case 'double':
        return <BedDouble className="w-5 h-5" />;
      case 'sofa_bed':
        return <Sofa className="w-5 h-5" />;
      case 'bunk_bed':
        return <Bed className="w-5 h-5" />;
      case 'crib':
        return <Baby className="w-5 h-5" />;
      default:
        return <Bed className="w-5 h-5" />;
    }
  };

  //   const getBedTypeName = (type) => {
  //     return getTranslation(language, `propertyInfo.${type}`);
  //   };

  return (
    <div className="w-full" onBlur={onBlur}>
      <h3 className="text-sm font-medium text-gray-700 mb-3">Bed Configuration</h3>

      <div className="space-y-3 mb-4">
        {beds?.map((bed) => (
          <div
            key={bed?.id}
            className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gray-100 rounded-full">{getBedIcon(bed?.type)}</div>
              <span className="text-sm font-medium">{bedsName[bed?.type]}</span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => updateBedCount(bed?.id, false)}
                className="p-1 text-gray-500 hover:text-timeless rounded-full hover:bg-gray-100"
                disabled={bed?.count <= 1}
              >
                <MinusCircle className={`w-5 h-5 ${bed?.count <= 1 ? 'opacity-50' : ''}`} />
              </button>

              <span className="w-6 text-center">{bed?.count}</span>

              <button
                type="button"
                onClick={() => updateBedCount(bed?.id, true)}
                className="p-1 text-gray-500 hover:text-timeless rounded-full hover:bg-gray-100"
              >
                <PlusCircle className="w-5 h-5" />
              </button>

              <button
                type="button"
                onClick={() => removeBed(bed?.id)}
                className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded-full"
              >
                <MinusCircle className="w-5 h-5" />
              </button>
            </div>
          </div>
        ))}

        {beds?.length === 0 && (
          <div className="p-4 text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            Add Bed
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => addBed('single')}
          className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 hover:border-timeless transition-colors"
        >
          <Bed className="w-4 h-4" />
          <span>Single</span>
        </button>

        <button
          type="button"
          onClick={() => addBed('double')}
          className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 hover:border-timeless transition-colors"
        >
          <BedDouble className="w-4 h-4" />
          <span>Double</span>
        </button>

        <button
          type="button"
          onClick={() => addBed('sofa_bed')}
          className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 hover:border-timeless transition-colors"
        >
          <Sofa className="w-4 h-4" />
          <span>Sofa Bed </span>
        </button>

        <button
          type="button"
          onClick={() => addBed('bunk_bed')}
          className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 hover:border-timeless transition-colors"
        >
          <Bed className="w-4 h-4" />
          <span>Bunk Bed</span>
        </button>

        <button
          type="button"
          onClick={() => addBed('crib')}
          className="flex items-center gap-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50 hover:border-timeless transition-colors"
        >
          <Baby className="w-4 h-4" />
          <span>Crib</span>
        </button>
      </div>
    </div>
  );
};

export default BedConfigurator;
